import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import { CareerChallengeAdminStore } from '../shared/career-challenge-admin.store';
import {
  applyDatePart,
  applyTimePart,
  formatTimeInput
} from '../shared/career-challenge-dates.util';

type WindowDateField = 'startsAt' | 'endsAt';

@Component({
  selector: 'app-career-challenge-schedule-tab',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatSnackBarModule
  ],
  templateUrl: './schedule-tab.component.html',
  styleUrl: './tab-shared.scss'
})
export class CareerChallengeScheduleTabComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(CsrWebApiService);
  private readonly store = inject(CareerChallengeAdminStore);
  private readonly fb = inject(FormBuilder);
  private readonly snackBar = inject(MatSnackBar);

  busy = false;
  challengeStatus = '';
  private baselineWindowsJson = '';
  readonly form = this.fb.nonNullable.group({
    windows: this.fb.array([this.buildWindow()])
  });

  get id(): string {
    return this.route.parent?.snapshot.paramMap.get('id') ?? '';
  }

  get windows(): FormArray {
    return this.form.controls.windows;
  }

  get loading() {
    return this.store.loading();
  }

  get schedulePhaseHelp(): string {
    switch (this.challengeStatus) {
      case 'active':
        return 'Start-related windows are locked while active. Saving date changes notifies registered participants.';
      case 'judging':
        return 'Registration/start windows are locked. Extending Ends will not reopen submissions.';
      case 'registration':
      case 'published':
        return 'Saving schedule changes on a live challenge notifies registered participants.';
      case 'completed':
      case 'archived':
        return 'Completed or archived challenges cannot change schedule.';
      default:
        return '';
    }
  }

  ngOnInit(): void {
    void this.reload();
  }

  async reload(): Promise<void> {
    const detail = await this.store.load(this.id, true);
    this.challengeStatus = String(detail?.status ?? '').toLowerCase();
    this.windows.clear();
    const list = detail?.scheduleWindows ?? [];
    if (!list.length) {
      this.windows.push(this.buildWindow());
      this.baselineWindowsJson = JSON.stringify(this.windows.getRawValue());
      this.applyWindowLocks();
      return;
    }
    for (const w of list) {
      this.windows.push(
        this.fb.group({
          key: this.fb.nonNullable.control(w.key, Validators.required),
          label: this.fb.nonNullable.control(w.label, Validators.required),
          startsAt: this.fb.control<Date | null>(w.startsAt ?? null),
          endsAt: this.fb.control<Date | null>(w.endsAt ?? null)
        })
      );
    }
    this.baselineWindowsJson = JSON.stringify(this.windows.getRawValue());
    this.applyWindowLocks();
  }

  addWindow(): void {
    this.windows.push(this.buildWindow());
    this.applyWindowLocks();
  }

  removeWindow(index: number): void {
    if (this.windows.length <= 1) {
      return;
    }
    this.windows.removeAt(index);
  }

  windowGroup(index: number): FormGroup {
    return this.windows.at(index) as FormGroup;
  }

  windowTime(index: number, field: WindowDateField): string {
    return formatTimeInput(this.windowGroup(index).controls[field].value as Date | null);
  }

  isWindowLocked(index: number): boolean {
    const key = String(this.windowGroup(index).controls['key'].value ?? '')
      .trim()
      .toLowerCase();
    if (this.challengeStatus === 'completed' || this.challengeStatus === 'archived') {
      return true;
    }
    if (this.challengeStatus === 'active') {
      return key === 'startsat' || key === 'starts' || key === 'start';
    }
    if (this.challengeStatus === 'judging') {
      return (
        key === 'startsat' ||
        key === 'starts' ||
        key === 'start' ||
        key === 'registrationopensat' ||
        key === 'registration' ||
        key === 'registration_opens' ||
        key === 'registrationclosesat' ||
        key === 'registration_closes'
      );
    }
    return false;
  }

  onWindowDatePicked(index: number, field: WindowDateField, datePart: Date | null): void {
    if (this.isWindowLocked(index)) {
      return;
    }
    const control = this.windowGroup(index).controls[field];
    const current = control.value as Date | null;
    control.setValue(applyDatePart(current, datePart));
  }

  onWindowTimeChanged(index: number, field: WindowDateField, time: string): void {
    if (this.isWindowLocked(index)) {
      return;
    }
    const control = this.windowGroup(index).controls[field];
    const current = control.value as Date | null;
    if (!time.trim()) {
      if (current) {
        const cleared = new Date(current.getTime());
        cleared.setHours(0, 0, 0, 0);
        control.setValue(cleared);
      }
      return;
    }
    control.setValue(applyTimePart(current, time));
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      return;
    }
    if (!this.confirmLiveScheduleChangesIfNeeded()) {
      return;
    }
    this.busy = true;
    try {
      const scheduleWindows = this.windows.getRawValue().map((w) => ({
        key: String(w.key).trim(),
        label: String(w.label).trim(),
        startsAt: (w.startsAt as Date | null) ?? null,
        endsAt: (w.endsAt as Date | null) ?? null
      }));
      await firstValueFrom(this.api.updateCareerChallengeSchedule(this.id, scheduleWindows));
      this.snackBar.open('Schedule saved', 'Close', { duration: 2200 });
      await this.reload();
    } catch (error) {
      this.snackBar.open(this.readError(error), 'Close', { duration: 3500 });
    } finally {
      this.busy = false;
    }
  }

  private applyWindowLocks(): void {
    for (let i = 0; i < this.windows.length; i++) {
      const group = this.windowGroup(i);
      const locked = this.isWindowLocked(i);
      ['startsAt', 'endsAt'].forEach((field) => {
        const control = group.controls[field];
        if (locked) {
          control.disable({ emitEvent: false });
        } else {
          control.enable({ emitEvent: false });
        }
      });
    }
  }

  private confirmLiveScheduleChangesIfNeeded(): boolean {
    if (
      !this.challengeStatus ||
      this.challengeStatus === 'draft' ||
      this.challengeStatus === 'completed' ||
      this.challengeStatus === 'archived'
    ) {
      return true;
    }
    const current = JSON.stringify(this.windows.getRawValue());
    if (current === this.baselineWindowsJson) {
      return true;
    }
    return window.confirm(
      `You are changing schedule windows on a live challenge (${this.challengeStatus}). ` +
        'Registered participants will be notified. Continue?'
    );
  }

  private buildWindow() {
    return this.fb.group({
      key: this.fb.nonNullable.control('', Validators.required),
      label: this.fb.nonNullable.control('', Validators.required),
      startsAt: this.fb.control<Date | null>(null),
      endsAt: this.fb.control<Date | null>(null)
    });
  }

  private readError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const detail = error.error?.detail ?? error.error?.title;
      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }
    }
    return 'Request failed';
  }
}